import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'

import ax from '@utils/axios'
import { handleError } from '@utils/helpers'
import { triggerFileDownload , calculateSHA256ForFile } from '@utils/fileHelpers'
import {useVendorStore} from '@stores/vendors'

export const useAssetStore = defineStore('asset', () => {
  const assets = ref([])
  const partnerAssetEngagements = ref([])
  const isLoading = ref(false)
  const error = ref(null)

  async function createAsset(payload) {
    isLoading.value = true
    error.value = null
    try {
      const payloadObj = Object.fromEntries(payload.entries())

      if('file' in payloadObj) {
        await uploadToS3(payloadObj)
      }

      const { data, status } = await ax.post('/v2/media-library', payloadObj)
      if (status === 201 || status === 200) {
        assets.value.push(data)
        return data
      } else {
        throw new Error(`Unexpected status code: ${status}`)
      }
    } catch (error) {
      error.value = error.response?.data?.message || error
    } finally {
      isLoading.value = false
    }
  }
  async function fetchAssets() {
    isLoading.value = true
    error.value = null
    try {
      const { data, status } = await ax.get('/v2/media-library')
      if (status === 200) {
        assets.value = data
      } else {
        throw new Error(`Unexpected status code: ${status}`)
      }
    } catch (error) {
      error.value = error.response?.data?.message || error
    } finally {
      isLoading.value = false
    }
  }

  async function getAssetById(assetId) {
    try {
      const { data } = await ax.get(`/v2/media-library/${assetId}`)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async function deleteAsset(id, throwError = false) {
    isLoading.value = true
    error.value = null
    try {
      await ax.delete(`/v2/media-library/${id}`)
      assets.value = assets.value.filter((asset) => asset.id !== id)
    } catch (error) {
      handleError(error)
      error.value = error.response?.data?.message || error
      if (throwError) {
        throw error
      }
    } finally {
      isLoading.value = false
    }
  }

  async function patchAsset(id, payload) {
    try {
      const payloadObj = Object.fromEntries(payload.entries())

      if('file' in payloadObj) {
        await uploadToS3(payloadObj)
      }

      const { data } = await ax.put(`/v2/media-library/${id}`, payloadObj)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async function duplicateAsset(id, throwError = false) {
    this.isLoading = true
    this.error = null
    try {
      const { data } = await ax.post(`/v2/media-library/${id}/copy`)
      return data
    } catch (error) {
      this.error = error.response?.data?.message || error
      if (throwError) {
        throw error
      }
    } finally {
      this.isLoading = false
    }
  }

  async function downloadAsset(id) {
    try {
      const response = await ax.get(`/v2/media-library/${id}/download`, {
        responseType: 'blob',
      })

      triggerFileDownload({
        filename: response.headers['content-disposition'].split('filename=')[1].replace(/"/g, ''),
        content: response.data,
        contentType: response.headers['content-type'],
        isBlob: true,
      })
    } catch (error) {
      error.value = error.response?.data?.message || error
      throw error
    }
  }

  async function createAssetTag(payload) {
    try {
      const { data } = await ax.post('/v2/media-library/tags', payload)
      return data
    } catch (error) {
      handleError(error)
      error.value = error.response?.data?.message || error
      throw error
    }
  }

  async function savePartnerCustomization(assetId, payload) {
    try {
      const { data } = await ax.put(`/v2/media-library/${assetId}/partner-customization`, payload)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async function addOrUpdateAssetEngagement(assetId) {
    try {
      const { data } = await ax.post(`/v2/media-library/${assetId}/view`)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async function getAllAssetEngagements() {
    try {
      const { data } = await ax.get('/v2/media-library/all-activity')
      partnerAssetEngagements.value = data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async function getAssetEngagements(assetId) {
    try {
      const response = await ax.get(`/v2/media-library/${assetId}/views`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async function uploadMedia(formData) {
    try {
      const { data } = await ax.post('/v2/media-library/direct-upload', formData)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const assetsGroupedByVendor = computed(() => {
    const vendorStore = useVendorStore()
    const partnerVendorsMap = new Map()

    assets.value.forEach((asset) => {
      const { vendor_id: assetVendorId } = asset
      if (!partnerVendorsMap.has(assetVendorId)) {
        partnerVendorsMap.set(assetVendorId, {
          ...vendorStore.vendors.find(vendor => vendor.id === assetVendorId),
          assets: []
        })
      }
      partnerVendorsMap.get(assetVendorId).assets.push(asset)
    })
    const vendorsArray = Array.from(partnerVendorsMap.values()).map(vendor => ({ ...vendor }))
    vendorsArray.forEach((vendor) => {
      const assetTags = vendor.assets.flatMap(asset => asset.tags)
      vendor.tags = Array.from(
        new Map(assetTags.map(tag => [tag.id, tag])).values()
      )
      vendor.updated_at = vendor.assets.reduce((latest, asset) => {
        return asset.timestamp > latest ? asset.timestamp : latest
      }, vendor.assets?.[0]?.timestamp)
    })

    return vendorsArray
  })

  function getAssetsByVendorId(vendorId) {
    return assets.value.filter(asset => asset.vendor_id === vendorId)
  }

  async function uploadToS3(payloadObj) {
    const file = payloadObj.file
    const sha256Hash = await calculateSHA256ForFile(file)

    const { data } = await ax.post('/v2/media-library/presigned-url', {
      'content_type': file.type,
      'checksum': sha256Hash,
    })
    const {url_res, file_id} = data

    const { url, fields } = url_res
    const formData = new FormData()
    Object.keys(fields).forEach(key => formData.append(key, fields[key]))
    formData.append('file', file)

    const s3UploadResponse =  await fetch(url, {
      method: 'POST',
      body: formData,
    })
    if (s3UploadResponse.ok) {
      delete payloadObj['file']

      payloadObj.file = {
        file_id: file_id,
        filename: file.name,
      }
    } else {
      throw new Error('File upload failed')
    }
  }

  function $reset() {
    assets.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    error,
    assets,
    isLoading,
    assetsGroupedByVendor,
    partnerAssetEngagements,
    patchAsset,
    createAsset,
    deleteAsset,
    fetchAssets,
    getAssetById,
    downloadAsset,
    createAssetTag,
    duplicateAsset,
    getAssetsByVendorId,
    getAssetEngagements,
    getAllAssetEngagements,
    savePartnerCustomization,
    addOrUpdateAssetEngagement,
    uploadMedia,
    $reset,
  }
})

// Enable HMR for Pinia
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAssetStore, import.meta.hot))
}
